/**
 * Thumbnail Generator
 * Generates static thumbnails and preview videos for visualizers
 * Uses OffscreenCanvas for headless rendering
 */

import { VisualizerRegistry } from '../visualizer.js';
import { generateMockFrequencyData, generateAnimatedFrequencyData, generateMockFeatures } from './mock-audio-data.js';

const THUMBNAIL_WIDTH = 320;
const THUMBNAIL_HEIGHT = 240;
const PREVIEW_DURATION = 5; // seconds
const PREVIEW_FPS = 30;
const INIT_TIMEOUT = 10000; // 10 seconds max wait for visualizer init

/**
 * Extract canonical default config from a visualizer class
 * Used for consistent thumbnail generation across all visualizers
 * @param {Function} VisualizerClass - The visualizer class
 * @returns {Object} - Canonical default config
 */
function getCanonicalDefaults(VisualizerClass) {
  const defaults = {
    exportMode: true,
    backgroundColor: '#000000'  // Canonical black background for consistency
  };

  // Extract UI defaults (with scale factors applied)
  if (VisualizerClass.getConfigUI) {
    try {
      const configUI = VisualizerClass.getConfigUI();
      for (const item of configUI) {
        if (item.id && item.default !== undefined) {
          defaults[item.id] = item.scale ? item.default * item.scale : item.default;
        }
      }
    } catch (e) {
      console.warn('[ThumbnailGenerator] Failed to get config UI defaults:', e);
    }
  }

  // Get visualizer-specific defaults (prototype method)
  // Note: getDefaultConfig is an instance method, but we can call it statically for extraction
  if (VisualizerClass.prototype && VisualizerClass.prototype.getDefaultConfig) {
    try {
      const vizDefaults = VisualizerClass.prototype.getDefaultConfig.call({});
      Object.assign(defaults, vizDefaults);
    } catch (e) {
      // Some visualizers may have getDefaultConfig that requires 'this' context
      // In that case, just skip - the base class defaults will be used
      console.debug('[ThumbnailGenerator] Could not extract prototype defaults:', e);
    }
  }

  // Force black background for consistency (override any extracted value)
  defaults.backgroundColor = '#000000';

  return defaults;
}

/**
 * ThumbnailGenerator - Generates static thumbnails and preview videos
 */
class ThumbnailGenerator {
  constructor() {
    this.webmMuxerLoaded = false;
  }

  /**
   * Check if WebGPU is available in this browser
   * @returns {Promise<boolean>}
   */
  async isWebGPUAvailable() {
    if (!navigator.gpu) return false;
    try {
      const adapter = await navigator.gpu.requestAdapter();
      return !!adapter;
    } catch {
      return false;
    }
  }

  /**
   * Check if VideoEncoder API is available
   * @returns {boolean}
   */
  isVideoEncoderAvailable() {
    return 'VideoEncoder' in window;
  }

  /**
   * Load webm-muxer from CDN
   * @returns {Promise<void>}
   */
  async loadWebmMuxer() {
    if (this.webmMuxerLoaded) return;

    // Check if already loaded
    if (window.WebMMuxer) {
      this.webmMuxerLoaded = true;
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/webm-muxer@5.0.1/build/webm-muxer.min.js';
      script.onload = () => {
        this.webmMuxerLoaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load webm-muxer'));
      document.head.appendChild(script);
    });
  }

  /**
   * Generate a static thumbnail for a visualizer
   * @param {string} visualizerName - Name of the visualizer
   * @param {AbortSignal} signal - For cancellation (optional)
   * @param {Object} customConfig - Custom config to use instead of defaults (for admin regeneration)
   * @returns {Promise<Blob>} - WebP image blob
   */
  async generateStaticThumbnail(visualizerName, signal = null, customConfig = null) {
    // Load visualizer class first to check context type
    const VisualizerClass = await VisualizerRegistry.load(visualizerName);
    if (!VisualizerClass) {
      throw new Error(`Visualizer not found: ${visualizerName}`);
    }

    // Check abort before expensive operations
    if (signal?.aborted) {
      throw new DOMException('Generation cancelled', 'AbortError');
    }

    const contextType = VisualizerClass.contextType || (VisualizerClass.isWebGL ? 'webgl' : '2d');
    const isWebGPU = contextType === 'webgpu';
    const isWebGL = contextType === 'webgl';
    const is2D = contextType === '2d';

    // Check WebGPU requirement
    if (isWebGPU) {
      const hasWebGPU = await this.isWebGPUAvailable();
      if (!hasWebGPU) {
        throw new Error('WebGPU not available for this visualizer');
      }
    }

    const canvas = new OffscreenCanvas(THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

    // For 2D visualizers only, pre-fill canvas with black as a fallback
    // (ensures black even if visualizer has issues with background clearing)
    // Note: Don't do this for WebGL - it would lock the canvas to 2D context
    if (is2D) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);
    }

    // Use custom config (for admin regeneration) or canonical defaults
    const config = customConfig
      ? { ...customConfig, exportMode: true }
      : getCanonicalDefaults(VisualizerClass);

    const visualizer = new VisualizerClass(canvas, config);

    try {
      // Wait for async initialization (WebGPU visualizers)
      if (visualizer.ready === false) {
        await this._waitForReady(visualizer, signal);
      }

      // Generate mock frequency data at a visually interesting moment
      // Use 70% intensity for a nice "peak" look
      const frequencyData = generateMockFrequencyData(0.7);
      const features = generateMockFeatures(frequencyData);

      // Draw frame
      const drawResult = visualizer.draw(frequencyData, features);
      if (drawResult instanceof Promise) {
        await drawResult;
      }

      // For WebGPU, add a small delay to ensure GPU work is fully flushed
      if (isWebGPU) {
        await new Promise(r => setTimeout(r, 50));
      }

      // Check abort after drawing
      if (signal?.aborted) {
        throw new DOMException('Generation cancelled', 'AbortError');
      }

      // Convert to WebP
      return await canvas.convertToBlob({ type: 'image/webp', quality: 0.85 });
    } finally {
      // Always cleanup
      if (visualizer.dispose) {
        visualizer.dispose();
      }
    }
  }

  /**
   * Generate a preview video for a visualizer
   * @param {string} visualizerName - Name of the visualizer
   * @param {AbortSignal} signal - For cancellation (optional)
   * @param {Object} customConfig - Custom config to use instead of defaults (for admin regeneration)
   * @returns {Promise<Blob>} - WebM video blob
   */
  async generatePreviewVideo(visualizerName, signal = null, customConfig = null) {
    // Check for VideoEncoder support
    if (!this.isVideoEncoderAvailable()) {
      throw new Error('VideoEncoder not supported in this browser');
    }

    // Load webm-muxer
    await this.loadWebmMuxer();

    // Load visualizer class first to check context type
    const VisualizerClass = await VisualizerRegistry.load(visualizerName);
    if (!VisualizerClass) {
      throw new Error(`Visualizer not found: ${visualizerName}`);
    }

    // Check abort
    if (signal?.aborted) {
      throw new DOMException('Generation cancelled', 'AbortError');
    }

    const contextType = VisualizerClass.contextType || (VisualizerClass.isWebGL ? 'webgl' : '2d');
    const isWebGPU = contextType === 'webgpu';
    const isWebGL = contextType === 'webgl';
    const is2D = contextType === '2d';

    // Check WebGPU requirement
    if (isWebGPU) {
      const hasWebGPU = await this.isWebGPUAvailable();
      if (!hasWebGPU) {
        throw new Error('WebGPU not available for this visualizer');
      }
    }

    const canvas = new OffscreenCanvas(THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);

    // For 2D visualizers only, pre-fill canvas with black as a fallback
    // Note: Don't do this for WebGL - it would lock the canvas to 2D context
    if (is2D) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);
    }

    // Use custom config (for admin regeneration) or canonical defaults
    const config = customConfig
      ? { ...customConfig, exportMode: true }
      : getCanonicalDefaults(VisualizerClass);

    const visualizer = new VisualizerClass(canvas, config);

    try {
      // Wait for async initialization
      if (visualizer.ready === false) {
        await this._waitForReady(visualizer, signal);
      }

      // Set up muxer to collect output using ArrayBufferTarget
      const target = new window.WebMMuxer.ArrayBufferTarget();
      const muxer = new window.WebMMuxer.Muxer({
        target,
        video: {
          codec: 'V_VP9',
          width: THUMBNAIL_WIDTH,
          height: THUMBNAIL_HEIGHT,
          frameRate: PREVIEW_FPS
        }
      });

      // Set up video encoder with error flag
      let videoEncoderError = null;
      const videoEncoder = new VideoEncoder({
        output: (chunk, meta) => {
          muxer.addVideoChunk(chunk, meta);
        },
        error: (e) => {
          console.error('Thumbnail encoder error:', e);
          videoEncoderError = e;
        }
      });

      // Configure VP9 for WebM
      const codecConfig = {
        codec: 'vp09.00.10.08', // VP9 Profile 0, Level 1.0
        width: THUMBNAIL_WIDTH,
        height: THUMBNAIL_HEIGHT,
        bitrate: 500_000, // 500 kbps for small file
        framerate: PREVIEW_FPS
      };

      // Check codec support
      const support = await VideoEncoder.isConfigSupported(codecConfig);
      if (!support.supported) {
        throw new Error('VP9 codec not supported');
      }

      videoEncoder.configure(codecConfig);

      const totalFrames = PREVIEW_DURATION * PREVIEW_FPS;
      const frameDurationMicros = 1_000_000 / PREVIEW_FPS;

      for (let i = 0; i < totalFrames; i++) {
        // Check abort
        if (signal?.aborted) {
          videoEncoder.close();
          throw new DOMException('Generation cancelled', 'AbortError');
        }

        // Generate animated frequency data
        const time = i / PREVIEW_FPS;
        const frequencyData = generateAnimatedFrequencyData(time);
        const features = generateMockFeatures(frequencyData);

        // Draw frame
        const drawResult = visualizer.draw(frequencyData, features);
        if (drawResult instanceof Promise) {
          await drawResult;
        }

        // Create and encode video frame
        const frame = new VideoFrame(canvas, {
          timestamp: i * frameDurationMicros,
          duration: frameDurationMicros
        });

        const isKeyframe = i % 30 === 0; // Keyframe every second
        videoEncoder.encode(frame, { keyFrame: isKeyframe });
        frame.close();

        // Yield periodically to prevent blocking
        if (i % 15 === 0) {
          await new Promise(r => setTimeout(r, 0));
        }

        // Apply backpressure if encoder queue is building up
        if (videoEncoder.encodeQueueSize > 10) {
          await videoEncoder.flush();
        }
      }

      // Finalize
      await videoEncoder.flush();
      if (videoEncoderError) {
        videoEncoder.close();
        throw new Error(`Thumbnail encoder error: ${videoEncoderError.message || videoEncoderError}`);
      }
      videoEncoder.close();
      muxer.finalize();

      // Get the buffer from the ArrayBufferTarget
      const buffer = target.buffer;
      return new Blob([buffer], { type: 'video/webm' });
    } finally {
      // Cleanup
      if (visualizer.dispose) {
        visualizer.dispose();
      }
    }
  }

  /**
   * Wait for visualizer to be ready (async init)
   * @param {Object} visualizer
   * @param {AbortSignal} signal
   */
  async _waitForReady(visualizer, signal) {
    const start = Date.now();
    while (!visualizer.ready && Date.now() - start < INIT_TIMEOUT) {
      if (signal?.aborted) {
        throw new DOMException('Generation cancelled', 'AbortError');
      }
      await new Promise(r => setTimeout(r, 50));
    }
    if (!visualizer.ready) {
      throw new Error('Visualizer initialization timeout');
    }
  }

}

export const thumbnailGenerator = new ThumbnailGenerator();
